import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicAccordion,
  BasicTable,
  Chip,
  Columns,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import componentConstants from '../../../core-utils/messages/componentConstants';
import MultiProgressBar from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/MultiProgressBar';
import { TextWithColorDot } from '@HomaHealth/ui-component-library';
import { getInfoIcon } from '../../../core-utils/helper-utils/infoIconHelper';

type RiskLevel = 'High' | 'Medium' | 'Low' | 'NA';
export interface ClauseDetailProps {
  title: string;
  analysis: string;
  metCount: number;
  unMetCount: number;
  chipValue: string;
  columns: Columns[];
  rowsData: any;
  tooltip: string;
}

const ClauseDetail: React.FC<ClauseDetailProps> = ({
  title,
  analysis,
  metCount,
  unMetCount,
  chipValue,
  columns,
  rowsData,
  tooltip,
}) => {
  const useStyles = makeStyles()(() => ({
    title: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '17px',
      color: patientAccessTheme.palette.text.mediumemphasis,
      margin: '0.5rem 0',
    },
    analysis: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '27px',
      color: patientAccessTheme.palette.text.highemphasis,
    },
  }));
  const { classes } = useStyles();
  const messages = componentConstants.ORGANISMS.CLAUSE_DETAILS;

  const getEvaluationMetrics = (value1: number, value2: number) => {
    const totalValue = value1 + value2;
    const value1Percent = (value1 / totalValue) * 100;
    const value2Percent = (value2 / totalValue) * 100;
    return (
      <Grid>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            margin: '0.5rem 0',
            gap: '1rem',
          }}
        >
          <TextWithColorDot
            color="#17A39C"
            text={
              <Typography fontSize="14px" fontType="regular" lineHeight="27px">
                {messages.FULLY_MET} <b>{value1}</b>
              </Typography>
            }
          />
          <TextWithColorDot
            color="#D46A6D"
            text={
              <Typography fontSize="14px" fontType="regular" lineHeight="27px">
                {messages.UNMET} <b>{value2}</b>
              </Typography>
            }
          />

          <Typography
            fontSize="14px"
            fontType="regular"
            lineHeight="27px"
            style={{ marginLeft: 'auto' }}
          >
            {messages.TOTAL} <b>{totalValue}</b>
          </Typography>
        </div>
        <MultiProgressBar
          colorAndCompleted={[
            {
              completed: value1Percent,
              color: '#17A39C',
            },
            {
              completed: value2Percent,
              color: '#D46A6D',
            },
          ]}
          height={'8px'}
          width="100%"
          backgroundColor={patientAccessTheme.palette.grey[100]}
          borderRadius="8px"
        />
      </Grid>
    );
  };
  const getChipComponent = (
    chipType: RiskLevel,
    label?: string | React.JSX.Element,
  ) => {
    switch (chipType) {
      case 'High':
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={patientAccessTheme.palette.AIQChips?.textColor1}
              >
                {label ?? 'High Risk'}
              </Typography>
            }
            backgroundColor={patientAccessTheme.palette.AIQChips?.background1}
            border="0.3px solid rgba(226, 84, 84, 0.5)"
            borderRadius="4px"
            padding="5px 8px"
            height="25px"
            width={label ? '120px' : '150px'}
          />
        );
      case 'Low':
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={patientAccessTheme.palette.AIQChips?.textColor2}
              >
                {label ?? 'Low Risk'}
              </Typography>
            }
            backgroundColor={patientAccessTheme.palette.AIQChips?.background2}
            border="0.3px solid rgba(0, 130, 114, 0.5)"
            borderRadius="4px"
            padding="5px 8px"
            height="25px"
            width={label ? '120px' : '150px'}
          />
        );

      case 'Medium':
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={patientAccessTheme.palette.primary[500]}
              >
                {label ?? 'Medium Risk'}
              </Typography>
            }
            backgroundColor={patientAccessTheme.palette.AIQChips?.background15}
            border={'0.3px solid' + patientAccessTheme.palette.primary[500]}
            borderRadius="4px"
            padding="5px 8px"
            height="25px"
            width={label ? '120px' : '150px'}
          />
        );

      default:
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={patientAccessTheme.palette.grey[900]}
              >
                {label ?? 'NA'}
              </Typography>
            }
            backgroundColor={patientAccessTheme.palette.grey[300]}
            border={'0.3px solid' + patientAccessTheme.palette.grey[900]}
            borderRadius="4px"
            padding="5px 8px"
            height="25px"
            width={label ? '120px' : '150px'}
          />
        );
    }
  };

  return (
    <Grid>
      <BasicAccordion
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography>{title}</Typography>
            <div style={{ right: '24px', position: 'absolute' }}>
              {getChipComponent(chipValue as RiskLevel)}
            </div>
          </div>
        }
        titleStyles={{ alignItems: 'center' }}
        content={
          <Grid
            container
            spacing={2}
            style={{ margin: 0 }}
            sx={{
              flexDirection: 'column',
            }}
          >
            <Grid>
              <Typography className={classes.title}>
                {messages.TITLE}
              </Typography>
              <Typography className={classes.analysis}>{analysis}</Typography>
            </Grid>
            <Grid>
              <Typography className={classes.title} style={{ display: 'flex' }}>
                {messages.EVALUATION}
                {getInfoIcon(messages.EVALUATION_TOOLTIP)}
              </Typography>
              {getEvaluationMetrics(metCount, unMetCount)}
            </Grid>
            <Grid>
              <Typography className={classes.title}>
                {messages.LIST_VIEW}
              </Typography>
              <BasicTable
                displayType="row"
                columns={columns}
                rowsData={rowsData}
              />
            </Grid>
          </Grid>
        }
        height="56px"
        tooltip={tooltip}
      />
    </Grid>
  );
};

export default ClauseDetail;
